import { motion, useReducedMotion } from 'framer-motion';
import SEO from '@/components/SEO';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { useLanguage } from '@/contexts/LanguageContext';
import historyImg1 from '@/assets/history/history-1.jpg';
import historyImg2 from '@/assets/history/history-2.jpg';
import historyImg3 from '@/assets/history/history-3.jpg';
import historyImg4 from '@/assets/history/history-4.jpg';
import historyImg5 from '@/assets/history/history-5.jpg';
import { HistoryTimeline } from '@/components/HistoryTimeline';

const historyCollageImages = [historyImg1, historyImg2, historyImg3, historyImg4, historyImg5];

const History = () => {
  const { t } = useLanguage();
  const shouldReduceMotion = useReducedMotion();

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title={t('Ιστορία', 'History')}
        description={t(
          'Η πορεία της Tesco Food Technology από το 1963 — ορόσημα, συνεργασίες και έργα που διαμόρφωσαν την ελληνική βιομηχανία τροφίμων και ποτών.',
          'The journey of Tesco Food Technology since 1963 — milestones, partnerships and projects that shaped the Greek food and beverage industry.'
        )}
        canonical="https://www.tft.gr/company/history"
      />
      <Navigation />

      <main className="flex-1">
        {/* Hero / Intro Section */}
        <section className="relative py-24 md:py-32 overflow-hidden">
          {/* Background Collage */}
          <div className="absolute inset-0 grid grid-cols-2 md:grid-cols-5 grid-rows-2 md:grid-rows-1 gap-0">
            {historyCollageImages.map((src, i) => (
              <motion.div
                key={i}
                initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, scale: 1.05 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1.2, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                className={`relative overflow-hidden ${i === 4 ? 'hidden md:block' : ''}`}
              >
                <img
                  src={src}
                  alt={t('Ιστορικό έργο TFT', 'TFT historical project')}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </motion.div>
            ))}
          </div>
          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#003F72]/90 via-[#003F72]/85 to-[#003F72]/85" />

          <div className="container mx-auto px-4 text-center max-w-5xl relative z-10">
            <motion.h1
              initial={shouldReduceMotion ? {} : { opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight tracking-tight"
              style={{ lineHeight: 1.2 }}
            >
              {t('Η Ιστορία μας', 'Our History')}
            </motion.h1>
            <motion.p
              initial={shouldReduceMotion ? {} : { opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.18 }}
              className="mt-4 text-lg md:text-2xl lg:text-3xl text-white font-semibold max-w-4xl mx-auto"
              style={{ lineHeight: 1.35 }}
            >
              {t(
                'Πάνω από 60 χρόνια τεχνολογικής υπεροχής στη Βιομηχανία Τροφίμων και Ποτών.',
                'Over 60 years of technological excellence in the Food and Beverage Industry.'
              )}
            </motion.p>
          </div>
        </section>

        {/* History Timeline Section */}
        <section className="py-12 md:py-16 bg-[#F5F7FB]">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12 max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-5xl font-bold text-[#003F72] mb-6">
                {t('Η Πορεία μας', 'Our Journey')}
              </h2>
              <p className="text-lg md:text-xl text-[#003F72]/70 mb-4" style={{ lineHeight: 1.3 }}>
                {t(
                  'Από το 1963, η Tesco Food Technology πρωτοπορεί στις λύσεις επεξεργασίας και συσκευασίας τροφίμων, συμβάλλοντας καθοριστικά στην ανάπτυξη και τον εκσυγχρονισμό της ελληνικής βιομηχανίας τροφίμων και ποτών.',
                  'Since 1963, Tesco Food Technology has been pioneering food processing and packaging solutions, playing a key role in the growth and modernization of the Greek food and beverage industry.'
                )}
              </p>
              <p className="text-lg md:text-xl text-[#003F72]/70 mb-4" style={{ lineHeight: 1.3 }}>
                {t(
                  'Διαθέτουμε μακροχρόνια και σημαντική εμπειρία και τεχνογνωσία, έχοντας υλοποιήσει πληθώρα βιομηχανικών έργων, καλύπτοντας με επιτυχία τις απαιτήσεις κάθε εποχής.',
                  'We have extensive and significant experience and know-how, having implemented numerous industrial projects, successfully meeting the demands of every era.'
                )}
              </p>
              <p className="text-base md:text-lg font-semibold text-[#003F72] bg-[#0077B6]/10 rounded-xl px-6 py-4 inline-block">
                {t('Με οδηγό μας το παρελθόν και σύμμαχό μας το μέλλον, είμαστε σε θέση να καλύπτουμε όλες τις ανάγκες μιας βιομηχανικής μονάδας, από την πιο μικρή έως την πιο μεγάλη.', 'With our past as a guide and our future as an ally, we are in a position to meet all the needs of an industrial unit, from the smallest to the largest.')}
              </p>
            </div>

            <HistoryTimeline />
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default History;
